import { request } from "./http";

export const authApi = {
  me: () => request("/auth/me"),
  login: (credentials) => request("/auth/login", { method: "POST", body: credentials }),
  register: (details) => request("/auth/register", { method: "POST", body: details }),
  logout: () => request("/auth/logout", { method: "POST" }),
  extensionToken: () => request("/auth/extension-token", { method: "POST" }),
  verifyEmail: (token) => request("/auth/verify-email", { method: "POST", body: { token } }),
  resendVerification: (email) => request("/auth/resend-verification", { method: "POST", body: { email } }),
  forgotPassword: (email) => request("/auth/forgot-password", { method: "POST", body: { email } }),
  resetPassword: (token, password) => request("/auth/reset-password", { method: "POST", body: { token, password } }),
  requestPasswordChange: () => request("/auth/change-password-request", { method: "POST" }),
  secureAccount: (token) => request("/auth/secure-account", { method: "POST", body: { token } }),
  deleteAccount: (password) => request("/auth/delete-account", { method: "POST", body: { password } }),
  disconnectGmail: () => request("/auth/google", { method: "DELETE" }),
  gmailConnectUrl: () => request("/auth/google/connect"),
  gmailWasntMe: (token) => request("/auth/google/wasnt-me", { method: "POST", body: { token } }),
};

const emptySiteConfig = () => ({
  appEnv: "",
  showEnvBanner: false,
  bannerText: "",
  contactEmail: "",
  extensionUrl: "",
});

let siteConfig;
// Public settings (env banner, contact email, extension store link), loaded once.
export const getSiteConfig = () => {
  siteConfig ||= request("/config").catch(() => {
    siteConfig = undefined;
    return emptySiteConfig();
  });
  return siteConfig;
};

export const usersApi = {
  update: (changes) => request("/users/me", { method: "PATCH", body: changes }),
  savedAnswers: () => request("/users/me/answers"),
  rememberAnswer: (label, answer) =>
    request("/users/me/answers", { method: "POST", body: { label, answer } }),
  updateAnswer: (id, label, answer) =>
    request(`/users/me/answers/${id}`, { method: "PATCH", body: { label, answer } }),
  forgetAnswer: (id) => request(`/users/me/answers/${id}`, { method: "DELETE" }),
  loadSampleData: () => request("/users/me/sample-data", { method: "POST" }),
  resetData: () => request("/users/me/data", { method: "DELETE" }),
  requestAccess: (reason) => request("/users/me/access-request", { method: "POST", body: { reason } }),
  memoryFromResume: () => request("/users/me/memory/from-resume", { method: "POST" }),
  memoryChat: (message) => request("/users/me/memory/chat", { method: "POST", body: { message } }),
  memoryRewrite: () => request("/users/me/memory/rewrite", { method: "POST" }),
};

export const adminApi = {
  stats: () => request("/admin/stats"),
  users: (params = {}) => request(`/admin/users?${new URLSearchParams(params)}`),
  updateUser: (id, changes) => request(`/admin/users/${id}`, { method: "PATCH", body: changes }),
  setAccess: (id, action, note = "", credits) =>
    request(`/admin/users/${id}/access`, { method: "POST", body: { action, note, credits } }),
  deleteUser: (id) => request(`/admin/users/${id}`, { method: "DELETE" }),
};

export const superadminApi = {
  logistics: () => request("/superadmin/logistics"),
  services: () => request("/superadmin/services"),
  users: (params = {}) => request(`/superadmin/users?${new URLSearchParams(params)}`),
  updateUser: (id, changes) => request(`/superadmin/users/${id}`, { method: "PATCH", body: changes }),
  setAccess: (id, action, note = "", credits) =>
    request(`/superadmin/users/${id}/access`, { method: "POST", body: { action, note, credits } }),
  softDeleteUser: (id) => request(`/superadmin/users/${id}/soft-delete`, { method: "POST" }),
  restoreUser: (id) => request(`/superadmin/users/${id}/restore`, { method: "POST" }),
  hardDeleteUser: (id) => request(`/superadmin/users/${id}`, { method: "DELETE" }),
};

export const jobsApi = {
  list: () => request("/jobs"),
  create: (fields) => request("/jobs", { method: "POST", body: fields }),
  update: (id, changes) => request(`/jobs/${id}`, { method: "PATCH", body: changes }),
  setStage: (id, stage) => request(`/jobs/${id}/stage`, { method: "PATCH", body: { stage } }),
  completeStep: (id, step, body) => request(`/jobs/${id}/steps/${step}`, { method: "POST", body }),
  undoApplied: (id) => request(`/jobs/${id}/steps/applied`, { method: "DELETE" }),
  addActivity: (id, text) => request(`/jobs/${id}/activity`, { method: "POST", body: { text } }),
  remove: (id) => request(`/jobs/${id}`, { method: "DELETE" }),
  resumeFile: (id, { format, suggestions, baseResumeId }) =>
    request(`/jobs/${id}/resume-file`, { method: "POST", body: { format, suggestions, baseResumeId }, raw: true }),
  coverLetterFile: (id, text) => request(`/jobs/${id}/cover-letter-file`, { method: "POST", body: { text }, raw: true }),
};

const DOC_PATHS = { resumes: "resumes", templates: "templates", transcripts: "transcripts", emailTemplates: "email-templates" };

const uploadFile = (path, file) => {
  const form = new FormData();
  form.append("file", file);
  return request(path, { method: "POST", body: form });
};

export const documentsApi = {
  list: () => request("/documents"),
  uploadResume: (file) => uploadFile("/documents/resumes", file),
  downloadResume: async (id) => (await request(`/documents/resumes/${id}/file`, { raw: true })).blob(),
  uploadTranscript: (file) => uploadFile("/documents/transcripts", file),
  downloadTranscript: async (id) => (await request(`/documents/transcripts/${id}/file`, { raw: true })).blob(),
  createTemplate: (template) => request("/documents/templates", { method: "POST", body: template }),
  updateTemplate: (id, changes) => request(`/documents/templates/${id}`, { method: "PATCH", body: changes }),
  createEmailTemplate: (template) => request("/documents/email-templates", { method: "POST", body: template }),
  updateEmailTemplate: (id, changes) => request(`/documents/email-templates/${id}`, { method: "PATCH", body: changes }),
  remove: (kind, id) => request(`/documents/${DOC_PATHS[kind]}/${id}`, { method: "DELETE" }),
  setDefault: (kind, id) => request(`/documents/${DOC_PATHS[kind]}/${id}/default`, { method: "POST" }),
};

const post = (path, body = {}) => request(path, { method: "POST", body });

export const platypusApi = {
  get: () => request("/platypus"),
  update: (settings) => request("/platypus", { method: "PUT", body: settings }),
  run: () => post("/platypus/run"),
  cancel: () => post("/platypus/cancel"),
  checkBoard: (url) => post("/platypus/boards/check", { url }),
};

// Back-compat alias (the agent was formerly called the Automator).
export const automatorApi = platypusApi;

export const assistantApi = {
  status: () => request("/assistant/status"),
  search: (filters) => request(`/assistant/search?${new URLSearchParams(filters)}`),
  parseJob: ({ url = "", text = "" }) => post("/assistant/parse-job", { url, text }),
  readPage: (url) => post("/assistant/read-page", { url }),
  coverLetter: (jobId, templateId) => post(`/assistant/jobs/${jobId}/cover-letter`, { templateId }),
  tailorResume: (jobId, resumeId) => post(`/assistant/jobs/${jobId}/tailor-resume`, { resumeId }),
  emailDraft: (jobId, contactId, emailTemplateId) => post(`/assistant/jobs/${jobId}/email-draft`, { contactId, emailTemplateId }),
  sendEmail: (jobId, payload) => post(`/assistant/jobs/${jobId}/send-email`, payload),
  answers: (jobId, questions) => post(`/assistant/jobs/${jobId}/answers`, { questions }),
  people: (jobId, kind, num = 10) =>
    request(`/assistant/jobs/${jobId}/people?kind=${encodeURIComponent(kind)}&num=${encodeURIComponent(num)}`),
  events: (jobId) => request(`/assistant/jobs/${jobId}/events`),
};
